// src/pages/Alerts.js
import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, query, orderBy, updateDoc, doc, onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import { format } from 'date-fns';

const SEVERITY = {
  info:     { bg: '#1a2a3a', border: '#2a4a6a', icon: 'ℹ', color: '#5599cc', label: 'Info' },
  warning:  { bg: '#2a2010', border: '#5a4010', icon: '⚠', color: '#f5a623', label: 'Warning' },
  critical: { bg: '#2a1010', border: '#5a2020', icon: '✕', color: '#ff6b6b', label: 'Critical' },
};

const TYPE_LABELS = {
  high_cancel_rate:  'High cancel rate',
  duplicate_customer:'Duplicate customer',
  large_commission:  'Large commission',
  csv_import:        'CSV import',
};

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all'); // all | unresolved | info | warning | critical
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'alerts'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setAlerts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  async function resolve(id) {
    await updateDoc(doc(db, 'alerts', id), { resolved: true });
  }

  async function resolveAll() {
    const unresolved = alerts.filter(a => !a.resolved);
    await Promise.all(unresolved.map(a => updateDoc(doc(db, 'alerts', a.id), { resolved: true })));
  }

  const filtered = alerts.filter(a => {
    if (filter === 'unresolved') return !a.resolved;
    if (filter === 'info' || filter === 'warning' || filter === 'critical') return a.severity === filter;
    return true;
  });

  const unresolvedCount = alerts.filter(a => !a.resolved).length;
  const warnCount  = alerts.filter(a => a.severity === 'warning' && !a.resolved).length;
  const infoCount  = alerts.filter(a => a.severity === 'info' && !a.resolved).length;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Alerts</h1>
          <p style={styles.sub}>{unresolvedCount} unresolved · {alerts.length} total</p>
        </div>
        {unresolvedCount > 0 && (
          <button style={styles.resolveAllBtn} onClick={resolveAll}>
            Resolve all
          </button>
        )}
      </div>

      {/* Summary cards */}
      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Unresolved</div>
          <div style={{ ...styles.summaryVal, color: '#fff' }}>{unresolvedCount}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Warnings</div>
          <div style={{ ...styles.summaryVal, color: '#f5a623' }}>{warnCount}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Info</div>
          <div style={{ ...styles.summaryVal, color: '#5599cc' }}>{infoCount}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Total alerts</div>
          <div style={{ ...styles.summaryVal, color: '#555' }}>{alerts.length}</div>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={styles.filterRow}>
        {['all', 'unresolved', 'critical', 'warning', 'info'].map(f => (
          <button
            key={f}
            style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading && <div style={styles.empty}>Loading alerts...</div>}

      <div style={styles.alertList}>
        {filtered.map(alert => {
          const sev = SEVERITY[alert.severity] || SEVERITY.info;
          const ts = alert.createdAt?.seconds
            ? format(new Date(alert.createdAt.seconds * 1000), 'MMM d, h:mm a')
            : '—';

          return (
            <div
              key={alert.id}
              style={{
                ...styles.alertCard,
                background: sev.bg,
                borderColor: sev.border,
                opacity: alert.resolved ? 0.45 : 1,
              }}
            >
              <div style={styles.alertLeft}>
                <div style={{ ...styles.alertIcon, color: sev.color }}>{sev.icon}</div>
                <div style={styles.alertBody}>
                  <div style={styles.alertMeta}>
                    <span style={{ ...styles.sevBadge, color: sev.color, borderColor: sev.border }}>
                      {sev.label}
                    </span>
                    <span style={styles.typeBadge}>
                      {TYPE_LABELS[alert.type] || alert.type}
                    </span>
                    {alert.repName && (
                      <span style={styles.repChip}>{alert.repName}</span>
                    )}
                  </div>
                  <div style={styles.alertTitle}>{alert.title}</div>
                  <div style={styles.alertMessage}>{alert.message}</div>
                  {alert.data && Object.keys(alert.data).length > 0 && (
                    <div style={styles.alertData}>
                      {Object.entries(alert.data).map(([k, v]) => (
                        <span key={k} style={styles.dataChip}>
                          {k}: <strong>{String(v)}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.alertRight}>
                <div style={styles.alertTime}>{ts}</div>
                {!alert.resolved ? (
                  <button style={styles.resolveBtn} onClick={() => resolve(alert.id)}>
                    Resolve
                  </button>
                ) : (
                  <span style={styles.resolvedTag}>Resolved</span>
                )}
              </div>
            </div>
          );
        })}

        {!loading && filtered.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>✓</div>
            <div>No alerts {filter !== 'all' ? `matching "${filter}"` : ''}</div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0' },
  resolveAllBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 8, padding: '8px 16px', color: '#888', fontSize: 12, cursor: 'pointer' },
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 },
  summaryCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '16px 18px' },
  summaryLabel: { fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, fontWeight: 500 },
  summaryVal: { fontSize: 26, fontWeight: 600 },
  filterRow: { display: 'flex', gap: 6, marginBottom: 16 },
  filterBtn: { background: 'transparent', border: '1px solid #1e1e2a', borderRadius: 8, padding: '6px 14px', color: '#555', fontSize: 12, cursor: 'pointer' },
  filterBtnActive: { background: '#1a1a28', borderColor: '#2a2a3a', color: '#00d4a0' },
  alertList: { display: 'flex', flexDirection: 'column', gap: 10 },
  alertCard: { border: '1px solid', borderRadius: 12, padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, transition: 'opacity .2s' },
  alertLeft: { display: 'flex', gap: 14, flex: 1 },
  alertIcon: { fontSize: 18, flexShrink: 0, marginTop: 2 },
  alertBody: { flex: 1 },
  alertMeta: { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' },
  sevBadge: { fontSize: 10, fontWeight: 600, padding: '2px 7px', border: '1px solid', borderRadius: 5, textTransform: 'uppercase', letterSpacing: '0.04em' },
  typeBadge: { fontSize: 10, color: '#555', background: '#1a1a24', padding: '2px 7px', borderRadius: 5 },
  repChip: { fontSize: 11, color: '#888', background: '#1e1e2a', padding: '2px 8px', borderRadius: 5 },
  alertTitle: { fontSize: 14, fontWeight: 500, color: '#fff', marginBottom: 4 },
  alertMessage: { fontSize: 13, color: '#888', lineHeight: 1.5 },
  alertData: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  dataChip: { fontSize: 11, color: '#666', background: '#0f0f18', padding: '3px 8px', borderRadius: 5 },
  alertRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 },
  alertTime: { fontSize: 11, color: '#444' },
  resolveBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 7, padding: '5px 12px', color: '#888', fontSize: 11, cursor: 'pointer' },
  resolvedTag: { fontSize: 11, color: '#3a4a3a', fontStyle: 'italic' },
  empty: { textAlign: 'center', color: '#444', padding: '60px 0', fontSize: 13 },
  emptyIcon: { fontSize: 32, color: '#1e3a1e', marginBottom: 12 },
};
